import { I18nDocumentName, MediaList, instructionMediaByGlobalDocument, supportedLanguages } from '@/util/i18n';
import { GetterTree, MutationTree, ActionTree, ActionContext, Module } from 'vuex';
import { Location } from 'vue-router';
import { RootState } from '../types';
import IdDocPhotos from '@/api/id-doc-photos';
import MnDocPhotos from '@/api/my-number-doc-photos';
import GpDocPhotos from '@/api/general-doc-photos';
import OpDocPhotos from '@/api/optional-doc-photos';
import FaceFrontPhotos from '@/api/face-front-photos';
import IdDocType from '@/resources/enum/IdDocType';
import MnDocType from '@/resources/enum/MnDocType';
import PhotoType from '@/resources/enum/PhotoType';
import ApiCancelError from '@/resources/error/ApiCancelError';
import { OsType, getOSType } from '@/util/device';
import RegionCode from '@/resources/enum/RegionCode';
import {
  isGeneralBook,
  isGeneralCard,
  isFaceRequired,
  isSkipDiagonal,
  isSkipBack,
  isBookType,
  isGlobal,
  isPassport,
  is,
  isFront,
  isFrontFace,
  getShape
} from '@/util/idDoc';
import vuei18n, { resetLocaleMessage } from '@/i18n/index';
import {
  i18nDocumentNames,
  instructionMediaByDocument as _instructionMediaByDocument,
  instructionMediaByGlobalDocument as _instructionMediaByGlobalDocument
} from '@/util/i18n';
import type { LocaleMessageObject } from 'vue-i18n';

export enum FacingMode {
  User = 'user',
  Environment = 'environment'
}

export enum IdealSize {
  Size320x240 = '320x240',
  Size640x480 = '640x480',
  Size800x600 = '800x600',
  Size1280x720 = '1280x720',
  Size1600x960 = '1600x960',
  Size1920x1080 = '1920x1080',
  Size3840x2160 = '3840x2160',
  Max = 'max'
}

export type ShootType =
  | 'none'
  | 'identity-document-front'
  | 'identity-document-diagonal'
  | 'identity-document-back'
  | 'mynumber-document-front'
  | 'mynumber-document-back'
  | 'general-purpose-document-page'
  | 'face-front'
  | 'face-action'
  | 'optional-document-page';

export interface CameraDevice {
  deviceId: string;
  label: string;
}

export type State = {
  idDocType: IdDocType;
  mnDocType: MnDocType;
  gpDocType: string;
  opDocType: string[];
  shootType: ShootType;

  gpDocPageCurr: number;
  gpDocPageLast: number;
  opDocTermCurr: number;
  opDocTermLast: number;
  opDocPageCurr: number;
  opDocPageLast: number;

  headerHeight: number;
  headerMarginHeight: number;
  shootFooterHeight: number;
  idDocAspectRatioCalc: number;
  idDocAspectRatioView: number;

  documentName: string;
  i18nKeyIntroductionTitle: string;
  i18nKeyIntroductionCaptionHeader: string;
  i18nKeyIntroductionCaption: string;
  i18nKeyIntroductionImage1: string;
  i18nKeyIntroductionImage2: string;
  i18nKeyIntroductionImage3: string;
  i18nKeyIntroductionImage4: string;
  i18nKeyIntroductionMovie: string;
  i18nKeyIntroductionDescriptionArray: string[];
  i18nKeyShootTitle: string;
  i18nKeyShootDescriptionArray: string[];
  i18nKeyConfirmationListArray: string[];
  i18nKeyErrorTitle: string;

  pictureWidth: number;
  pictureHeight: number;
  displayWidth: number;
  displayHeight: number;
  clippingDisplayWidth: number;
  rawBase64Width: number;
  rawBase64Height: number;
  pictureIdDocCenter: number;
  displayIdDocCenter: number;

  isNotFound: boolean;
  isResize: boolean;
  isStart: boolean;
  isShooting: boolean;
  isShooted: boolean;

  detectCount: number;
  isPermittedCameraAccess: boolean;
  isDetectableIdDocFront: boolean;
  isDetectableIdDocDiagonal: boolean;
  isDetectableIdDocBack: boolean;
  isDetectStop: boolean;
  isDetectStart: boolean;
  isDetectCapture: boolean;
  isDetectSuccess: boolean;
  detectWarningCode: string;

  videoDevices: CameraDevice[];
  actualFacingMode: VideoFacingModeEnum | 'none';
  // FacingModeごとに選択したカメラをおぼておくためにRecord
  selectedDeviceIndex: Record<FacingMode, number>;
  availableCameras: Record<FacingMode, string[]>;
  unavailableCameras: Record<FacingMode, string[]>;
  readyCameraModeModal: boolean;
  showCameraModeModal: boolean;
  isCameraChanging: boolean;
  isRefreshRequesting: boolean;
  isFaceActionSkipLoading: boolean;

  retryCount: number;
  photoID: { [key: string]: number };
  trimmedPhoto: { [key: string]: string };
};

function createState(): State {
  const state: State = {} as State;
  clearState(state);
  return state;
}

function clearState(state: State): void {
  state.idDocType = IdDocType.Unknown;
  state.mnDocType = MnDocType.Unknown;
  state.gpDocType = 'unknown';
  state.opDocType = [];
  state.shootType = 'none';
  state.gpDocPageCurr = 0;
  state.gpDocPageLast = 0;
  state.opDocTermCurr = 0;
  state.opDocTermLast = 0;
  state.opDocPageCurr = 0;
  state.opDocPageLast = 0;
  state.headerHeight = 0;
  state.headerMarginHeight = 0;
  state.shootFooterHeight = 0;
  state.idDocAspectRatioCalc = 1.0;
  state.idDocAspectRatioView = 1.0;
  state.documentName = '';
  state.i18nKeyIntroductionTitle = '';
  state.i18nKeyIntroductionCaption = '';
  state.i18nKeyIntroductionCaptionHeader = '';
  state.i18nKeyIntroductionImage1 = '';
  state.i18nKeyIntroductionImage2 = '';
  state.i18nKeyIntroductionImage3 = '';
  state.i18nKeyIntroductionImage4 = '';
  state.i18nKeyIntroductionMovie = '';
  state.i18nKeyIntroductionDescriptionArray = [];
  state.i18nKeyShootTitle = '';
  state.i18nKeyShootDescriptionArray = [];
  state.i18nKeyConfirmationListArray = [];
  state.i18nKeyErrorTitle = '';
  state.pictureWidth = 0;
  state.pictureHeight = 0;
  state.displayWidth = 0;
  state.displayHeight = 0;
  state.clippingDisplayWidth = 0;
  state.rawBase64Width = 0;
  state.rawBase64Height = 0;
  state.pictureIdDocCenter = 0;
  state.displayIdDocCenter = 0;
  state.isNotFound = false;
  state.isResize = false;
  state.isStart = false;
  state.isShooting = false;
  state.isShooted = false;
  state.detectCount = 0;
  state.isPermittedCameraAccess = false;
  state.isDetectableIdDocFront = false;
  state.isDetectableIdDocDiagonal = false;
  state.isDetectableIdDocBack = false;
  state.isDetectStop = false;
  state.isDetectStart = false;
  state.isDetectCapture = false;
  state.isDetectSuccess = false;
  state.detectWarningCode = '';
  state.retryCount = 0;
  state.photoID = {};
  state.trimmedPhoto = {};
  state.videoDevices = [];
  state.actualFacingMode = 'none';
  state.isCameraChanging = false;
  state.selectedDeviceIndex = {
    [FacingMode.Environment]: 0,
    [FacingMode.User]: 0
  };
  state.availableCameras = {
    [FacingMode.Environment]: [],
    [FacingMode.User]: []
  };
  state.unavailableCameras = {
    [FacingMode.Environment]: [],
    [FacingMode.User]: []
  };
  state.readyCameraModeModal = false;
  state.showCameraModeModal = false;
  state.isRefreshRequesting = false;
  state.isFaceActionSkipLoading = false;
}

function clearI18nKey(state: State): void {
  state.i18nKeyIntroductionTitle = '';
  state.i18nKeyIntroductionCaption = '';
  state.i18nKeyIntroductionCaptionHeader = '';
  state.i18nKeyIntroductionImage1 = '';
  state.i18nKeyIntroductionImage2 = '';
  state.i18nKeyIntroductionImage3 = '';
  state.i18nKeyIntroductionImage4 = '';
  state.i18nKeyIntroductionMovie = '';
  state.i18nKeyIntroductionDescriptionArray = [];
  state.i18nKeyShootTitle = '';
  state.i18nKeyShootDescriptionArray = [];
  state.i18nKeyConfirmationListArray = [];
  state.i18nKeyErrorTitle = '';
}

export const getters: GetterTree<State, RootState> = {
  isPC: (state: State): boolean => getOSType() === OsType.PC,
  isAndroid: (state: State): boolean => getOSType() === OsType.Android,
  idDocType: (state: State): IdDocType => state.idDocType,
  mnDocType: (state: State): MnDocType => state.mnDocType,
  gpDocType: (state: State): string => state.gpDocType,
  opDocType: (state: State): string[] => state.opDocType,
  isPermittedCameraAccess: (state: State): boolean => state.isPermittedCameraAccess,
  isGpdoc: (state: State): boolean => state.shootType === 'general-purpose-document-page',
  isOpdoc: (state: State): boolean => state.shootType === 'optional-document-page',
  gpdocPageCurr: (state: State): number => state.gpDocPageCurr,
  gpdocPageLast: (state: State): number => state.gpDocPageLast,
  opdocTermCurr: (state: State): number => state.opDocTermCurr,
  opdocTermLast: (state: State): number => state.opDocTermLast,
  opdocPageCurr: (state: State): number => state.opDocPageCurr,
  opdocPageLast: (state: State): number => state.opDocPageLast,
  isGeneralBook: (state): boolean => isGeneralBook(state.idDocType),
  isGeneralCard: (state): boolean => isGeneralCard(state.idDocType),
  isA4Shape: (state): boolean => getShape(state.idDocType) === 'a4',
  isGlobal: (state): boolean => isGlobal(state.idDocType),
  isBookType: (state): boolean => isBookType(state.idDocType),
  isIdDocDiagonal: (state): boolean => state.shootType === 'identity-document-diagonal',
  isIdDocFront: (state): boolean => state.shootType === 'identity-document-front',
  isIdDocBack: (state): boolean => state.shootType === 'identity-document-back',
  isIdDoc: (_, getters) => getters.isIdDocFront || getters.isIdDocBack || getters.isIdDocDiagonal,
  showShootTypeConfirmation: (state, getters): boolean => {
    if (getters.isIdDocFront && state.isDetectableIdDocFront) return true;
    if (getters.isIdDocDiagonal && state.isDetectableIdDocDiagonal) return true;
    if (getters.isIdDocBack && state.isDetectableIdDocBack) return true;
    return false;
  },
  isFaceActionSkipLoading: (state: State): boolean => state.isFaceActionSkipLoading,

  facingMode: (state: State, getters): VideoFacingModeEnum | 'none' => {
    const isIdDocFront: boolean = state.shootType === 'identity-document-front';
    const isIdDocDiagonal: boolean = state.shootType === 'identity-document-diagonal';
    const isIdDocBack: boolean = state.shootType === 'identity-document-back';
    const isMnDocFront: boolean = state.shootType === 'mynumber-document-front';
    const isMnDocBack: boolean = state.shootType === 'mynumber-document-back';
    const isFaceFront: boolean = state.shootType === 'face-front';
    const isFaceAction: boolean = state.shootType === 'face-action';
    if (isIdDocFront) return 'environment';
    if (isIdDocDiagonal) return 'environment';
    if (isIdDocBack) return 'environment';
    if (isMnDocFront) return 'environment';
    if (isMnDocBack) return 'environment';
    if (getters['isGpdoc']) return 'environment';
    if (getters['isOpdoc']) return 'environment';
    if (isFaceFront) return 'user';
    if (isFaceAction) return 'user';
    return 'none';
  },

  idealSize: (state: State): IdealSize => {
    const isFaceAction: boolean = state.shootType === 'face-action';
    if (isFaceAction) return IdealSize.Size640x480;
    return IdealSize.Max;
  },

  displayScale: (state: State, getters, __, rootGetters): number => {
    if (!rootGetters['auth/kycOrgConfigEnableShrinkFrame']) return 1.0;
    const isPC: boolean = getters['isPC'];
    const isFaceFront: boolean = state.shootType === 'face-front';
    const isFaceAction: boolean = state.shootType === 'face-action';
    if (isPC || isFaceFront || isFaceAction) return 1.0;
    return 1.2;
  },

  isMirror: (state: State, getters): boolean => {
    if (state.actualFacingMode === 'user') return true;
    return getters['facingMode'] === 'user';
  },

  // カメラモードの手動切り替えができるかどうか
  canChangeCameraModeManually: (state: State): boolean => {
    if (getOSType() === OsType.Android) return true;
    if (getOSType() === OsType.iOS) return true;
    if (getOSType() === OsType.iPad) return true;
    return false;
  },
  faceOvalWidth: (_, getters): number => Math.ceil(getters['faceOvalHeight'] * (7 / 9)),
  faceOvalHeight: (state: State): number => {
    // 画面横サイズに比例したマスクの高さを計算
    const preMaskWidth: number = Math.ceil(state.displayWidth * 0.7);
    const maskHeight1: number = Math.ceil(preMaskWidth * (9 / 7));
    // 画面縦サイズに比例したマスクの高さを計算
    const maskHeight2: number = Math.ceil(state.displayHeight * (5 / 8));
    // 画面縦の中央にマスクをおく上で全てを表示するためにとりうる最大の高さを計算
    const imaginaryHeaderFooterHeight: number = 100;
    const maxHeight: number = Math.max(0, state.displayHeight - imaginaryHeaderFooterHeight * 2);
    // 上記高さのうち小さい方を採用
    return Math.min(maskHeight1, maskHeight2, maxHeight);
  },

  locationParams: (state: State, getters): { [key: string]: string } => {
    const routeParams: { [key: string]: string } = {};
    const isGpDocUnknown: boolean = state.gpDocType === 'unknown';
    const isOpDocUnknown: boolean = state.opDocType.length <= 0;
    if (!isGpDocUnknown) routeParams['gpdoc_page'] = `${state.gpDocPageCurr}`;
    if (!isOpDocUnknown) routeParams['opdoc_term'] = `${state.opDocTermCurr}`;
    if (!isOpDocUnknown) routeParams['opdoc_page'] = `${state.opDocPageCurr}`;
    return routeParams;
  },

  locationFirst: (state: State, getters): Location => {
    const isIdDocMnGeneral: boolean = state.idDocType === IdDocType.MnGeneral;
    const isIdDocGpGeneral: boolean = state.idDocType === IdDocType.GpGeneral;
    const isMnDocUnknown: boolean = state.mnDocType === MnDocType.Unknown;
    const isGpDocUnknown: boolean = state.gpDocType === 'unknown';
    let routeName: string = '';

    const routeParams: { [key: string]: string } = Object.assign({}, getters['locationParams']);
    if (!isIdDocMnGeneral && !isIdDocGpGeneral) {
      // 通常の本人確認書類からの撮影開始
      routeName = 'identity-document-front-introduction';
    } else if (isIdDocMnGeneral && !isMnDocUnknown) {
      // 個人番号確認書類からの撮影開始
      routeName = 'mynumber-document-front-introduction';
    } else if (isIdDocGpGeneral && !isGpDocUnknown) {
      // 汎用書類からの撮影開始
      routeName = 'general-purpose-document-page-introduction';
      routeParams['gpdoc_page'] = '1';
    } else {
      // とりあえず完了 ここには来ないはず
      routeName = 'application-completion';
    }

    return { name: routeName, params: routeParams };
  },

  locationIntroduction: (state: State, getters): Location => {
    const routeName: string = `${state.shootType}-introduction`;
    return { name: routeName, params: getters['locationParams'] };
  },

  locationShootTypeConfirmation: (state: State, getters): Location => {
    const routeName: string = `${state.shootType}-shoot-type-confirmation`;
    return { name: routeName, params: getters['locationParams'] };
  },

  locationShoot: (state: State, getters): Location => {
    const routeName: string = `${state.shootType}-shooting`;
    return { name: routeName, params: getters['locationParams'] };
  },

  locationConfirmation: (state: State, getters): Location => {
    const routeName: string = `${state.shootType}-confirmation`;
    return { name: routeName, params: getters['locationParams'] };
  },

  locationError: (state: State, getters): Location => {
    const routeName: string = `${state.shootType}-error`;
    return { name: routeName, params: getters['locationParams'] };
  },

  locationNext: (state: State, getters, __, rootGetters): Location => {
    let routeName: string = '';
    const routeParams: { [key: string]: string } = Object.assign({}, getters['locationParams']);

    if (getters['isGpdoc']) {
      if (state.gpDocPageCurr < state.gpDocPageLast) {
        routeName = `general-purpose-document-page-shooting`;
        routeParams['gpdoc_page'] = `${state.gpDocPageCurr + 1}`;
      } else {
        return getters['locationSkip'];
      }
    } else if (getters['isOpdoc']) {
      if (state.opDocPageCurr < state.opDocPageLast) {
        routeName = `optional-document-page-shooting`;
        routeParams['opdoc_page'] = `${state.opDocPageCurr + 1}`;
      } else {
        return getters['locationSkip'];
      }
    } else {
      const isIdDocFront: boolean = state.shootType === 'identity-document-front';
      const isIdDocDiagonal: boolean = state.shootType === 'identity-document-diagonal';
      const isIdDocBack: boolean = state.shootType === 'identity-document-back';
      const isMnDocFront: boolean = state.shootType === 'mynumber-document-front';
      const isMnDocBack: boolean = state.shootType === 'mynumber-document-back';
      const isFaceFront: boolean = state.shootType === 'face-front';
      const isFaceAction: boolean = state.shootType === 'face-action';

      const isMnDocResidenceCertificate: boolean = state.mnDocType === MnDocType.ResidenceCertificate;

      const isIdDocUnknown: boolean = state.idDocType === IdDocType.Unknown;
      const isMnDocUnknown: boolean = state.mnDocType === MnDocType.Unknown;
      const isOpDocUnknown: boolean = state.opDocType.length <= 0;

      // 本人確認書類斜面撮影スキップフラグ
      const isSkipIdDocDiagonal: boolean = isSkipDiagonal(state.idDocType);

      // 本人確認書類裏面撮影スキップフラグ
      const isSkipIdDocBack: boolean = isSkipBack(state.idDocType);

      // 顔撮影フラグ
      const isStepFace: boolean = isFaceRequired(state.idDocType);

      // パッシブ判定フラグ
      const isPassiveMode: boolean = rootGetters['auth/kycOrgConfigFaceVerificationType'] === 1;

      // 遷移先設定
      if (isIdDocFront) routeName = 'identity-document-diagonal-introduction';
      if (isIdDocDiagonal) routeName = 'identity-document-back-introduction';
      if (isIdDocBack) routeName = 'mynumber-document-front-introduction';
      if (isMnDocFront) routeName = 'mynumber-document-back-introduction';
      if (isMnDocBack) routeName = 'face-front-introduction';
      if (isFaceFront) routeName = isPassiveMode ? 'optional-document-page-introduction' : 'face-action-introduction';
      if (isFaceAction) routeName = 'optional-document-page-introduction';
      // 本人確認書類の撮影が必要なければスキップ
      if (isIdDocUnknown && isIdDocFront) routeName = '';
      if (isIdDocUnknown && isIdDocDiagonal) routeName = '';
      if (isIdDocUnknown && isIdDocBack) routeName = '';
      if (isIdDocUnknown && routeName === 'identity-document-front-introduction') routeName = 'mynumber-document-front-introduction'; // eslint-disable-line prettier/prettier
      // スキップフラグを確認して本人確認書類斜面を省略
      if (isSkipIdDocDiagonal && isIdDocDiagonal) routeName = '';
      if (isSkipIdDocDiagonal && routeName === 'identity-document-diagonal-introduction')
        routeName = 'identity-document-back-introduction';
      // スキップフラグを確認して本人確認書類裏面を省略
      if (isSkipIdDocBack && isIdDocBack) routeName = '';
      if (isSkipIdDocBack && routeName === 'identity-document-back-introduction') routeName = 'mynumber-document-front-introduction'; // eslint-disable-line prettier/prettier
      // 個人番号書類の撮影が必要なければスキップ
      if (isMnDocUnknown && isMnDocFront) routeName = '';
      if (isMnDocUnknown && isMnDocBack) routeName = '';
      if (isMnDocUnknown && routeName === 'mynumber-document-front-introduction') routeName = 'face-front-introduction';
      // 住民票では裏面を省略
      if (isMnDocResidenceCertificate && isMnDocBack) routeName = '';
      if (isMnDocResidenceCertificate && routeName === 'mynumber-document-back-introduction') routeName = 'face-front-introduction'; // eslint-disable-line prettier/prettier
      // 顔撮影の必要がなければスキップ
      if (!isStepFace && isFaceFront) routeName = '';
      if (!isStepFace && isFaceAction) routeName = '';
      if (!isStepFace && routeName === 'face-front-introduction') routeName = 'optional-document-page-introduction';
      // スキップフラグを確認して補助書類撮影を省略
      if (isOpDocUnknown && routeName === 'optional-document-page-introduction') routeName = 'application-completion';

      // 補助書類撮影の場合は最初から
      if (routeName === 'optional-document-page-introduction') {
        routeParams['opdoc_term'] = '1';
        routeParams['opdoc_page'] = '1';
      }
    }

    return { name: routeName, params: routeParams };
  },

  locationSkip: (state: State, getters): Location => {
    let routeName: string = '';
    const routeParams: { [key: string]: string } = Object.assign({}, getters['locationParams']);

    if (getters['isOpdoc'] && state.opDocTermCurr < state.opDocTermLast) {
      routeName = `optional-document-page-introduction`;
      routeParams['opdoc_term'] = `${state.opDocTermCurr + 1}`;
      routeParams['opdoc_page'] = '1';
    } else {
      routeName = 'application-completion';
    }

    return { name: routeName, params: routeParams };
  },

  i18nKeyIntroductionImage1: (state: State): string => state.i18nKeyIntroductionImage1,
  i18nKeyIntroductionImage2: (state: State): string => state.i18nKeyIntroductionImage2,
  i18nKeyIntroductionImage3: (state: State): string => state.i18nKeyIntroductionImage3,
  i18nKeyIntroductionImage4: (state: State): string => state.i18nKeyIntroductionImage4,
  i18nKeyIntroductionMovie: (state: State): string => state.i18nKeyIntroductionMovie,
  i18nKeyIntroductionDescriptionArray: (state: State): string[] => state.i18nKeyIntroductionDescriptionArray,
  i18nKeyShootTitle: (state: State): string => state.i18nKeyShootTitle,
  i18nKeyShootDescriptionArray: (state: State): string[] => state.i18nKeyShootDescriptionArray,
  i18nKeyConfirmationListArray: (state: State): string[] => state.i18nKeyConfirmationListArray,
  i18nKeyErrorTitle: (state: State): string => state.i18nKeyErrorTitle,
  i18nKeyIntroductionTitle: (state: State): string => state.i18nKeyIntroductionTitle,
  i18nKeyIntroductionCaption: (state: State): string => state.i18nKeyIntroductionCaption,
  i18nKeyIntroductionCaptionHeader: (state: State): string => state.i18nKeyIntroductionCaptionHeader
};

export const mutations: MutationTree<State> = {
  setIdDocType(state: State, payload: IdDocType): void {
    state.idDocType = payload;
  },

  setMnDocType(state: State, payload: MnDocType): void {
    state.mnDocType = payload;
  },

  setGpDocType(state: State, payload: string): void {
    state.gpDocType = payload;
  },

  setOpDocType(state: State, payload: string[]): void {
    state.opDocType = payload;
    state.opDocTermLast = payload.length;
  },

  resetDocTypes(state: State): void {
    state.idDocType = IdDocType.Unknown;
    state.mnDocType = MnDocType.Unknown;
    state.gpDocType = 'unknown';
    state.opDocType = [];
  },

  setShootType(state: State, payload: ShootType): void {
    state.shootType = payload;
  },

  setGpDocPageCurr(state: State, payload: number): void {
    state.gpDocPageCurr = payload;
  },

  setGpDocPageLast(state: State, payload: number): void {
    state.gpDocPageLast = payload;
  },

  setOpDocTermCurr(state: State, payload: number): void {
    state.opDocTermCurr = payload;
  },

  setOpDocPageCurr(state: State, payload: number): void {
    state.opDocPageCurr = payload;
  },

  setOpDocPageLast(state: State, payload: number): void {
    state.opDocPageLast = payload;
  },

  setMountedSettingSize(
    state: State,
    payload: { isPC: boolean; isGpDoc: boolean; isOpDoc: boolean; isGeneralBook: boolean; isA4Shape: boolean }
  ): void {
    const isIdDocFront: boolean = state.shootType === 'identity-document-front';
    const isIdDocDiagonal: boolean = state.shootType === 'identity-document-diagonal';
    const isIdDocBack: boolean = state.shootType === 'identity-document-back';
    const isMnDocFront: boolean = state.shootType === 'mynumber-document-front';

    const isMnDocResidenceCertificate: boolean = state.mnDocType === MnDocType.ResidenceCertificate;

    const isIdDoc: boolean = isIdDocFront || isIdDocDiagonal || isIdDocBack;
    const isGpDoc: boolean = payload.isGpDoc;
    const isOpDoc: boolean = payload.isOpDoc;
    const isGeneralBook: boolean = payload.isGeneralBook;
    const isA4Shape: boolean = payload.isA4Shape;
    const isIdDocPassport: boolean = isPassport(state.idDocType);
    const isIdDocSpecialPermanentResidentCertificate: boolean = is(
      state.idDocType,
      'special-permanent-resident-certificate'
    );

    state.isDetectStart = ((): boolean => {
      if (state.isDetectStop) return false;
      if (isIdDocFront && state.isDetectableIdDocFront) return true;
      if (isIdDocDiagonal && state.isDetectableIdDocDiagonal) return true;
      if (isIdDocBack && state.isDetectableIdDocBack) return true;
      return false;
    })();

    // ヘッダとフッダの高さ
    state.headerHeight = payload.isPC ? 80 : state.isDetectStart ? 70 : 50;
    state.shootFooterHeight = payload.isPC ? 140 : state.isDetectStart ? 0 : 100;
    // ヘッダの追加マージン 斜め撮影では画面上に文言の追加 自動撮影ではメッセージボックス分の余裕
    state.headerMarginHeight = isIdDocDiagonal && !isGeneralBook ? 57 : state.isDetectStart ? 40 : 0;
    // 手動斜め撮影の時は、ボタンの位置が変わる (パスポート/特別永住者証明書は除く)
    if (
      !state.isDetectStart &&
      isIdDocDiagonal &&
      !payload.isPC &&
      !isIdDocPassport &&
      !isIdDocSpecialPermanentResidentCertificate &&
      !isGeneralBook
    )
      state.shootFooterHeight = 100 + Math.min(60, Math.max(0, state.displayHeight - 454));

    state.idDocAspectRatioView = 0.63 * (isIdDocDiagonal ? 0.8 : 1.0);
    if (isIdDocFront && isIdDocPassport) state.idDocAspectRatioView = 0.7;
    if (isIdDocDiagonal && isIdDocPassport) state.idDocAspectRatioView = 0.75;
    if (isIdDocBack && isIdDocPassport) state.idDocAspectRatioView = 0.7;
    if (isMnDocFront && isMnDocResidenceCertificate) state.idDocAspectRatioView = 1.41;
    if (isGpDoc || isOpDoc || isGeneralBook || isA4Shape) state.idDocAspectRatioView = 1.0;
    // 本人確認書類は撮影位置をサーバから受け取る必要があり、その計算に必要なカード縦横比をサーバと合わせるために、表示用と別の値を用意する
    state.idDocAspectRatioCalc = state.idDocAspectRatioView;
    if (isIdDoc && !isGeneralBook && !isA4Shape) state.idDocAspectRatioCalc = isIdDocPassport ? 0.7 : 0.63;
  },

  setMountedSettingI18nKey(
    state: State,
    payload: { isPC: boolean; isGpDoc: boolean; isOpDoc: boolean; isGlobal: boolean; regionCode: RegionCode }
  ): void {
    /**
     * 書類のタイプ（カード・手帳・A4・パスポート・個人番号書類・汎用書類・追加書類・顔）× 撮影画面の種類（表斜裏）の組み合わせで、どのような文言を表示するかを決定する
     * @Todo Global対応
     */

    const { isGlobal, isGpDoc, isOpDoc, isPC, regionCode } = payload;
    const shootType = state.shootType;
    const documentType = isPassport(state.idDocType) ? 'passport' : getShape(state.idDocType);
    const deviceType = isPC ? 'pc' : 'sp';
    const i18nDocumentName = (i18nDocumentNames[state.idDocType] +
      (state.isDetectStart ? '-auto' : '')) as I18nDocumentName;
    const documentName = vuei18n.t(`documentNames.${i18nDocumentName}`) as string;
    const documentShortName = vuei18n.t(`documentShortNames.${i18nDocumentName}`) as string;
    const variableReplacement = {
      documentName,
      documentShortName
    };
    const mnDocumentName = (
      {
        [MnDocType.MyNumberCard]: 'my-number-card',
        [MnDocType.NotificationCard]: 'mynumber-document-notification-card',
        [MnDocType.ResidenceCertificate]: 'mynumber-document-residence-certificate',
        [MnDocType.Unknown]: 'mynumber-document-unknown'
      } as const
    )[state.mnDocType];

    const instructionMediaByDocument = isGlobal ? _instructionMediaByGlobalDocument : _instructionMediaByDocument;
    // 書類ごとのインストラクション画像・動画
    const instructionMedia = instructionMediaByDocument[i18nDocumentName]?.[deviceType] || {};
    // 汎用手帳・カードのインストラクション画像・動画
    const generalCardInstructionMedia = instructionMediaByDocument['general-card']?.[deviceType] || {};
    const generalBookInstructionMedia = instructionMediaByDocument['general-book']?.[deviceType] || {};
    // 個人番号書類のインストラクション画像・動画
    const mnInstructionMedia = instructionMediaByDocument[mnDocumentName]?.[deviceType] || {};
    const applyI18nInstructionMedia = (list: MediaList | undefined) => {
      if (!list) return;
      state.i18nKeyIntroductionImage1 = list.i18nKeyIntroductionImage1 || '';
      state.i18nKeyIntroductionImage2 = list.i18nKeyIntroductionImage2 || '';
      state.i18nKeyIntroductionImage3 = list.i18nKeyIntroductionImage3 || '';
      state.i18nKeyIntroductionImage4 = list.i18nKeyIntroductionImage4 || '';
      state.i18nKeyIntroductionMovie =
        list.i18nKeyIntroductionMovie?.[vuei18n.locale as keyof typeof supportedLanguages] || '';
    };

    // キー情報リセット
    clearI18nKey(state);
    // 例外文言で上書きされているのでロケールメッセージを初期状態にリセット
    resetLocaleMessage();

    // 書類ごとの例外文言の適用
    const exceptions = vuei18n.t(`exceptions.${i18nDocumentName}`);
    if (exceptions) vuei18n.mergeLocaleMessage(vuei18n.locale, exceptions as LocaleMessageObject);

    // カードタイプ・個人番号書類
    // 2024/02時点で、A4タイプは個人番号書類と差はないので、個人番号書類と同じ文言を適用
    if (documentType === 'card' || documentType === 'a4' || documentType === 'mnGeneral') {
      if (shootType === 'identity-document-front' || shootType === 'mynumber-document-front') {
        state.i18nKeyIntroductionTitle = vuei18n.t('202305_002', variableReplacement) as string;
        state.i18nKeyIntroductionCaption = vuei18n.t('202305_003', variableReplacement) as string;
        state.i18nKeyIntroductionDescriptionArray = vuei18n.t('202305_004', variableReplacement).toString().split('\n');
        state.i18nKeyShootTitle = vuei18n.t('202305_007', variableReplacement) as string;
        state.i18nKeyShootDescriptionArray = vuei18n.t('202305_008', variableReplacement).toString().split('\n');
        state.i18nKeyConfirmationListArray = [
          vuei18n.t('202305_014', variableReplacement),
          vuei18n.t('202305_015', variableReplacement),
          vuei18n.t('202305_016', variableReplacement),
          vuei18n.t('202305_017', variableReplacement)
        ] as string[];
        state.i18nKeyErrorTitle = vuei18n.t('202305_007', variableReplacement) as string;
        applyI18nInstructionMedia(
          instructionMedia['front'] || mnInstructionMedia['front'] || generalCardInstructionMedia['front']
        );
      }

      if (shootType === 'identity-document-diagonal') {
        state.i18nKeyIntroductionTitle = vuei18n.t('202309_224', variableReplacement) as string;
        state.i18nKeyIntroductionCaption = vuei18n.t('202309_225', variableReplacement) as string;
        state.i18nKeyIntroductionDescriptionArray = {
          sp: vuei18n.t('202309_001', variableReplacement).toString().split('\n'),
          pc: vuei18n.t('202309_002', variableReplacement).toString().split('\n')
        }[deviceType];
        state.i18nKeyShootTitle = vuei18n.t('202309_227', variableReplacement) as string;
        state.i18nKeyShootDescriptionArray = vuei18n.t('202309_0065', variableReplacement).toString().split('\n');
        state.i18nKeyConfirmationListArray = [vuei18n.t('202309_007', variableReplacement)] as string[];
        state.i18nKeyErrorTitle = vuei18n.t('202309_227', variableReplacement) as string;
        applyI18nInstructionMedia(
          instructionMedia['diagonal'] || mnInstructionMedia['diagonal'] || generalCardInstructionMedia['diagonal']
        );
      }

      if (shootType === 'identity-document-back' || shootType === 'mynumber-document-back') {
        state.i18nKeyIntroductionTitle = vuei18n.t('202309_235', variableReplacement) as string;
        state.i18nKeyIntroductionCaption = vuei18n.t('202309_236', variableReplacement) as string;
        state.i18nKeyIntroductionDescriptionArray = vuei18n.t('202309_008', variableReplacement).toString().split('\n');
        state.i18nKeyShootTitle = vuei18n.t('202309_235', variableReplacement) as string;
        state.i18nKeyShootDescriptionArray = vuei18n.t('202309_009', variableReplacement).toString().split('\n');
        state.i18nKeyConfirmationListArray = [
          vuei18n.t('202309_010', variableReplacement),
          vuei18n.t('202309_0105', variableReplacement),
          vuei18n.t('202309_011', variableReplacement)
        ] as string[];
        state.i18nKeyErrorTitle = vuei18n.t('202309_235', variableReplacement) as string;
        applyI18nInstructionMedia(
          instructionMedia['back'] || mnInstructionMedia['back'] || generalCardInstructionMedia['back']
        );
      }
    }

    // パスポートタイプ
    if (documentType === 'passport') {
      if (shootType === 'identity-document-front') {
        state.i18nKeyIntroductionTitle = vuei18n.t('202309_245', variableReplacement) as string;
        state.i18nKeyIntroductionCaption = vuei18n.t('202309_246', variableReplacement) as string;
        state.i18nKeyIntroductionDescriptionArray = {
          sp: vuei18n.t('202309_247', variableReplacement).toString().split('\n'),
          pc: vuei18n.t('202309_252', variableReplacement).toString().split('\n')
        }[deviceType];
        state.i18nKeyShootTitle = vuei18n.t('202309_254', variableReplacement) as string;
        state.i18nKeyShootDescriptionArray = vuei18n.t('202309_255', variableReplacement).toString().split('\n');
        state.i18nKeyConfirmationListArray = [
          vuei18n.t('202309_015', variableReplacement),
          vuei18n.t('202309_016', variableReplacement),
          vuei18n.t('202309_260', variableReplacement)
        ] as string[];
        state.i18nKeyErrorTitle = vuei18n.t('202309_254', variableReplacement) as string;
        applyI18nInstructionMedia(instructionMedia['front']);
      }

      if (shootType === 'identity-document-diagonal') {
        state.i18nKeyIntroductionTitle = vuei18n.t('202309_266', variableReplacement) as string;
        state.i18nKeyIntroductionCaption = vuei18n.t('202309_278', variableReplacement) as string;
        state.i18nKeyIntroductionDescriptionArray = {
          sp: vuei18n.t('202309_273', variableReplacement).toString().split('\n'),
          pc: vuei18n.t('202309_274', variableReplacement).toString().split('\n')
        }[deviceType];
        state.i18nKeyShootTitle = vuei18n.t('202309_276', variableReplacement) as string;
        state.i18nKeyShootDescriptionArray = vuei18n.t('202309_279', variableReplacement).toString().split('\n');
        state.i18nKeyConfirmationListArray = [
          vuei18n.t('202309_284', variableReplacement),
          vuei18n.t('202309_285', variableReplacement)
        ] as string[];
        state.i18nKeyErrorTitle = vuei18n.t('202309_276', variableReplacement) as string;
        applyI18nInstructionMedia(instructionMedia['diagonal']);
      }

      if (shootType === 'identity-document-back') {
        state.i18nKeyIntroductionTitle = vuei18n.t('202309_288', variableReplacement) as string;
        state.i18nKeyIntroductionCaption = vuei18n.t('202309_289', variableReplacement) as string;
        state.i18nKeyIntroductionDescriptionArray = {
          sp: vuei18n.t('202309_290', variableReplacement).toString().split('\n'),
          pc: vuei18n.t('202309_295', variableReplacement).toString().split('\n')
        }[deviceType];
        state.i18nKeyShootTitle = vuei18n.t('202309_297', variableReplacement) as string;
        state.i18nKeyShootDescriptionArray = vuei18n.t('202309_298', variableReplacement).toString().split('\n');
        state.i18nKeyConfirmationListArray = [
          vuei18n.t('202309_303', variableReplacement),
          vuei18n.t('202309_304', variableReplacement)
        ] as string[];
        state.i18nKeyErrorTitle = vuei18n.t('202309_297', variableReplacement) as string;
        applyI18nInstructionMedia(instructionMedia['back']);
      }
    }

    // 手帳タイプ
    if (documentType === 'book') {
      if (shootType === 'identity-document-front') {
        state.i18nKeyIntroductionTitle = vuei18n.t('202309_017', variableReplacement) as string;
        state.i18nKeyIntroductionCaption = vuei18n.t('202403_001', variableReplacement) as string;

        state.i18nKeyIntroductionDescriptionArray = [
          vuei18n.t('202309_026', variableReplacement),
          vuei18n.t('202309_312', variableReplacement)
        ] as string[];
        state.i18nKeyShootTitle = vuei18n.t('202309_018', variableReplacement) as string;
        state.i18nKeyShootDescriptionArray = {
          sp: vuei18n.t('202309_314', variableReplacement).toString().split('\n'),
          pc: vuei18n.t('202309_315', variableReplacement).toString().split('\n')
        }[deviceType];
        state.i18nKeyConfirmationListArray = [
          vuei18n.t('202309_320', variableReplacement),
          vuei18n.t('202309_321', variableReplacement)
        ] as string[];
        state.i18nKeyErrorTitle = vuei18n.t('202309_018', variableReplacement) as string;
        applyI18nInstructionMedia(instructionMedia['front'] || generalBookInstructionMedia['front']);
      }

      if (shootType === 'identity-document-diagonal') {
        state.i18nKeyIntroductionTitle = vuei18n.t('202309_020', variableReplacement) as string;
        state.i18nKeyIntroductionCaption = vuei18n.t('202309_324', variableReplacement) as string;

        state.i18nKeyIntroductionDescriptionArray = [
          vuei18n.t('202309_329', variableReplacement),
          vuei18n.t('202309_330', variableReplacement)
        ] as string[];
        state.i18nKeyShootTitle = vuei18n.t('202309_021', variableReplacement) as string;
        state.i18nKeyShootDescriptionArray = {
          sp: vuei18n.t('202309_334', variableReplacement).toString().split('\n'),
          pc: []
        }[deviceType];
        state.i18nKeyConfirmationListArray = [
          vuei18n.t('202309_339', variableReplacement),
          vuei18n.t('202309_340', variableReplacement),
          vuei18n.t('202309_341', variableReplacement)
        ] as string[];
        state.i18nKeyErrorTitle = vuei18n.t('202309_021', variableReplacement) as string;
        applyI18nInstructionMedia(generalBookInstructionMedia['diagonal']);
      }

      if (shootType === 'identity-document-back') {
        state.i18nKeyIntroductionTitle = vuei18n.t('202309_023', variableReplacement) as string;
        state.i18nKeyIntroductionCaption = vuei18n.t('202403_002', variableReplacement) as string;

        state.i18nKeyIntroductionDescriptionArray = [
          vuei18n.t('202309_027', variableReplacement).toString(),
          vuei18n.t('202309_028', variableReplacement).toString()
        ];
        state.i18nKeyShootTitle = vuei18n.t('202309_024', variableReplacement) as string;
        state.i18nKeyShootDescriptionArray = {
          sp: vuei18n.t('202309_349', variableReplacement).toString().split('\n'),
          pc: vuei18n.t('202309_350', variableReplacement).toString().split('\n')
        }[deviceType] as string[];
        state.i18nKeyConfirmationListArray = [vuei18n.t('202309_355', variableReplacement)] as string[];
        state.i18nKeyErrorTitle = vuei18n.t('202309_024', variableReplacement) as string;
        applyI18nInstructionMedia(generalBookInstructionMedia['back']);
      }
    }

    // 汎用書類
    if (documentType === 'gpGeneral') {
      const page: number = state.gpDocPageCurr;
      if (shootType === 'general-purpose-document-page') {
        state.i18nKeyIntroductionTitle = vuei18n.t('202309_410') as string;

        state.i18nKeyIntroductionDescriptionArray = [
          {
            sp: vuei18n.t('202309_413').toString(),
            pc: vuei18n.t('202309_414').toString()
          }[deviceType],
          vuei18n.t('202309_415').toString()
        ];
        state.i18nKeyShootTitle = vuei18n.t('202309_417', { _連番: page }) as string;
        state.i18nKeyShootDescriptionArray = {
          sp: vuei18n.t('202309_418').toString().split('\n'),
          pc: vuei18n.t('202309_419').toString().split('\n')
        }[deviceType];
        state.i18nKeyConfirmationListArray = [
          vuei18n.t('202309_425'),
          vuei18n.t('202309_426'),
          vuei18n.t('202309_427')
        ] as string[];
        state.i18nKeyErrorTitle = vuei18n.t('202309_417', { _連番: page }) as string;
      }
    }

    // 追加書類
    if (isOpDoc) {
      const page: number = state.opDocTermCurr;
      if (shootType === 'optional-document-page') {
        state.i18nKeyIntroductionTitle = vuei18n.t('202309_358', {
          _連番: page
        }) as string;

        state.i18nKeyIntroductionDescriptionArray = {
          sp: vuei18n.t('202309_368').toString().split('\n'),
          pc: vuei18n.t('202309_380').toString().split('\n')
        }[deviceType];
        state.i18nKeyShootTitle = vuei18n.t('202309_383', { _連番: page }) as string;
        state.i18nKeyShootDescriptionArray = {
          sp: vuei18n.t('202309_385').toString().split('\n'),
          pc: vuei18n.t('202309_389').toString().split('\n')
        }[deviceType];
        state.i18nKeyConfirmationListArray = [
          vuei18n.t('202309_394'),
          vuei18n.t('202309_395'),
          vuei18n.t('202309_396')
        ] as string[];
        state.i18nKeyErrorTitle = vuei18n.t('202309_383', { _連番: page }) as string;
      }
    }

    // 顔正面
    if (shootType === 'face-front') {
      state.i18nKeyIntroductionTitle = vuei18n.t('202309_443') as string;
      state.i18nKeyIntroductionCaption = vuei18n.t('202309_440') as string;
      state.i18nKeyIntroductionDescriptionArray = vuei18n.t('202309_441').toString().split('\n');
      state.i18nKeyShootTitle = vuei18n.t('202309_443') as string;
      state.i18nKeyShootDescriptionArray = vuei18n.t('202309_444').toString().split('\n');
      state.i18nKeyConfirmationListArray = [
        vuei18n.t('202309_449'),
        vuei18n.t('202309_450'),
        vuei18n.t('202309_451')
      ] as string[];
      state.i18nKeyErrorTitle = vuei18n.t('202309_443') as string;
      applyI18nInstructionMedia(instructionMediaByDocument['face']?.[deviceType]['face-front']);
    }

    if (shootType === 'face-action') {
      state.i18nKeyIntroductionTitle = vuei18n.t('202309_454') as string;
      state.i18nKeyIntroductionCaption = vuei18n.t('202309_455') as string;
      state.i18nKeyIntroductionDescriptionArray = {
        sp: vuei18n.t('202309_462').toString().split('\n'),
        pc: vuei18n.t('202309_463').toString().split('\n')
      }[deviceType];
      state.i18nKeyErrorTitle = vuei18n.t('202309_454') as string;
      applyI18nInstructionMedia(instructionMediaByDocument['face']?.[deviceType]['face-action']);
    }

    // 配列の中身が空の場合は削除
    state.i18nKeyIntroductionDescriptionArray = state.i18nKeyIntroductionDescriptionArray.filter((v) => v);
    state.i18nKeyShootDescriptionArray = state.i18nKeyShootDescriptionArray.filter((v) => v);
    state.i18nKeyConfirmationListArray = state.i18nKeyConfirmationListArray.filter((v) => v);
  },

  clearSetup(state: State): void {
    state.pictureWidth = 0;
    state.pictureHeight = 0;
    state.displayWidth = 0;
    state.displayHeight = 0;
    state.clippingDisplayWidth = 0;
    state.rawBase64Width = 0;
    state.rawBase64Height = 0;
    state.pictureIdDocCenter = 0;
    state.displayIdDocCenter = 0;
    state.i18nKeyIntroductionImage1 = '';
    state.i18nKeyIntroductionImage2 = '';
    state.i18nKeyIntroductionImage3 = '';
    state.i18nKeyIntroductionImage4 = '';
    state.i18nKeyIntroductionMovie = '';
  },

  pictureWidth(state: State, payload: number): void {
    state.pictureWidth = payload;
  },

  pictureHeight(state: State, payload: number): void {
    state.pictureHeight = payload;
  },

  displayWidth(state: State, payload: number): void {
    state.displayWidth = payload;
  },

  displayHeight(state: State, payload: number): void {
    state.displayHeight = payload;
  },

  clippingDisplayWidth(state: State, payload: number): void {
    state.clippingDisplayWidth = payload;
  },

  rawBase64Width(state: State, payload: number): void {
    state.rawBase64Width = payload;
  },

  rawBase64Height(state: State, payload: number): void {
    state.rawBase64Height = payload;
  },

  pictureIdDocCenter(state: State, payload: number): void {
    state.pictureIdDocCenter = payload;
  },

  displayIdDocCenter(state: State, payload: number): void {
    state.displayIdDocCenter = payload;
  },

  isNotFound(state: State, payload: boolean): void {
    state.isNotFound = payload;
  },

  isResize(state: State, payload: boolean): void {
    state.isResize = payload;
  },

  isStart(state: State, payload: boolean): void {
    state.isStart = payload;
  },

  isShooting(state: State, payload: boolean): void {
    state.isShooting = payload;
  },

  isShooted(state: State, payload: boolean): void {
    state.isShooted = payload;
  },

  addDetectCount(state: State): void {
    state.detectCount++;
  },

  resetDetectCount(state: State): void {
    state.detectCount = 0;
  },

  isPermittedCameraAccess(state: State, payload: boolean): void {
    state.isPermittedCameraAccess = payload;
  },

  isDetectableIdDocFront(state: State, payload: boolean): void {
    state.isDetectableIdDocFront = payload;
  },

  isDetectableIdDocDiagonal(state: State, payload: boolean): void {
    state.isDetectableIdDocDiagonal = payload;
  },

  isDetectableIdDocBack(state: State, payload: boolean): void {
    state.isDetectableIdDocBack = payload;
  },

  isDetectStop(state: State, payload: boolean): void {
    state.isDetectStop = payload;
  },

  isDetectCapture(state: State, payload: boolean): void {
    state.isDetectCapture = payload;
  },

  isDetectSuccess(state: State, payload: boolean): void {
    state.isDetectSuccess = payload;
  },

  detectWarningCode(state: State, payload: string): void {
    state.detectWarningCode = payload;
  },

  addRetryCount(state: State): void {
    state.retryCount++;
  },

  resetRetryCount(state: State): void {
    state.retryCount = 0;
  },

  photoID(state: State, payload: { key: string; value: number }): void {
    state.photoID = Object.assign({}, state.photoID, {
      [payload.key]: payload.value
    });
  },

  trimmedPhoto(state: State, payload: { key: string; value: string }): void {
    state.trimmedPhoto = Object.assign({}, state.trimmedPhoto, {
      [payload.key]: payload.value
    });
  },

  clearState(state) {
    clearState(state);
  },

  setVideoDevices(state: State, payload: MediaDeviceInfo[]): void {
    state.videoDevices = payload.map((device) => ({ deviceId: device.deviceId, label: device.label }));
  },

  setActualFacingMode(state: State, payload: VideoFacingModeEnum | 'none'): void {
    state.actualFacingMode = payload;
  },

  setIsCameraChanging(state: State, payload: boolean): void {
    state.isCameraChanging = payload;
  },

  setCameraModeIndex(state: State, payload: { mode: FacingMode; index: number }): void {
    state.selectedDeviceIndex[payload.mode] = payload.index;
  },

  setAvailableCamera(state: State, payload: { mode: FacingMode; deviceId: string }): void {
    if (state.availableCameras[payload.mode].includes(payload.deviceId) === false) {
      state.availableCameras[payload.mode].push(payload.deviceId);
    }
  },

  setUnavailableCamera(state: State, payload: { mode: FacingMode; deviceId: string }): void {
    if (state.unavailableCameras[payload.mode].includes(payload.deviceId) === false) {
      state.unavailableCameras[payload.mode].push(payload.deviceId);
    }
  },

  setReadyCameraModeModal(state: State, payload: boolean): void {
    state.readyCameraModeModal = payload;
  },

  setShowCameraModeModal(state: State, payload: boolean): void {
    state.showCameraModeModal = payload;
  },

  setIsRefreshRequesting(state: State, payload: boolean): void {
    state.isRefreshRequesting = payload;
  },

  setFaceActionSkipLoading(state: State, payload: boolean): void {
    state.isFaceActionSkipLoading = payload;
  }
};

export const actions: ActionTree<State, RootState> = {
  clearState(context: ActionContext<State, RootState>): void {
    context.commit('clearState');
  },

  // ページマウント時の設定 パスから情報を設定する
  async mountedSetting(context: ActionContext<State, RootState>, payload: string): Promise<void> {
    const keys: string[] = payload.split('/');
    context.commit('setShootType', `${keys[1]}-${keys[2]}`);
    if (context.getters['isGpdoc']) context.commit('setGpDocPageCurr', parseInt(keys[3]));
    if (context.getters['isOpdoc']) context.commit('setOpDocTermCurr', parseInt(keys[3]));
    if (context.getters['isOpdoc']) context.commit('setOpDocPageCurr', parseInt(keys[4]));
    const isPC: boolean = context.getters['isPC'];
    const isGpDoc: boolean = context.getters['isGpdoc'];
    const isOpDoc: boolean = context.getters['isOpdoc'];
    const isGeneralBook: boolean = context.getters['isGeneralBook'];
    const isA4Shape: boolean = context.getters['isA4Shape'];
    const isGlobal: boolean = context.getters['isGlobal'];
    const regionCode: RegionCode = context.rootState.auth.regionCode;
    context.commit('setMountedSettingSize', { isPC, isGpDoc, isOpDoc, isGeneralBook, isA4Shape });
    context.commit('setMountedSettingI18nKey', { isPC, isGpDoc, isOpDoc, isGlobal, regionCode });
    // 不正なページはエラーに飛ばす 例外的にエラーページは文言確認用途で表示できる
    const isNotFound: boolean = context.getters['locationNext'].name === '';
    const isPageError: boolean = keys[keys.length - 1] === 'error';
    context.commit('isNotFound', isNotFound && !isPageError);
  },

  // 撮影開始通信 ランダム枠の位置を取得する
  async storeStart(context: ActionContext<State, RootState>): Promise<void> {
    const isPC: boolean = context.getters['isPC'];

    const isIdDocFront: boolean = context.state.shootType === 'identity-document-front';
    const isIdDocDiagonal: boolean = context.state.shootType === 'identity-document-diagonal';
    const isIdDocBack: boolean = context.state.shootType === 'identity-document-back';
    const isMnDocFront: boolean = context.state.shootType === 'mynumber-document-front';
    const isMnDocBack: boolean = context.state.shootType === 'mynumber-document-back';
    const isFaceFront: boolean = context.state.shootType === 'face-front';

    const isIdDoc: boolean = isIdDocFront || isIdDocDiagonal || isIdDocBack;
    const isMnDoc: boolean = isMnDocFront || isMnDocBack;
    const isGpDoc: boolean = context.getters['isGpdoc'];
    const isOpDoc: boolean = context.getters['isOpdoc'];
    const isDocument: boolean = isIdDoc || isMnDoc || isGpDoc || isOpDoc;

    const displayScale: number = context.getters['displayScale'];
    const pictureWidth: number = context.state.pictureWidth / displayScale;
    const pictureHeight: number = context.state.pictureHeight / displayScale;
    const displayWidth: number = context.state.displayWidth;
    const displayHeight: number = context.state.displayHeight;

    if (isDocument) {
      const isGeneralBook: boolean = context.getters['isGeneralBook'];
      const isA4Shape: boolean = context.getters['isA4Shape'];
      const isGlobal: boolean = context.getters['isGlobal'];
      const _isFront: boolean = isFront(context.state.idDocType); // 表面撮影方式（容貌なし）かどうか
      const _isFrontFace: boolean = isFrontFace(context.state.idDocType); // 表面撮影方式（容貌あり）かどうか

      // ヘッダとフッダの高さを計算
      let headerHeight: number = context.state.headerHeight + context.state.headerMarginHeight;
      let footerHeight: number = context.state.shootFooterHeight;
      if (isIdDoc && !isGeneralBook && !isA4Shape) {
        // 本人確認書類の自動斜め撮影の時は、本人確認書類が中央にくるようにする
        const headerFooterHeightHelf: number = Math.floor((headerHeight + footerHeight) / 2);
        if (context.state.isDetectStart && isIdDocDiagonal) headerHeight = footerHeight = headerFooterHeightHelf;

        // 中心から遠ざかるとピントがあいにくくなるようなので、移動範囲を80%に狭める
        // 移動範囲ができるだけ中央に来るようにする
        const movementRatio: number = isPC ? 1.0 : 0.8;
        const movementRange: number = (displayHeight - headerHeight - footerHeight) * movementRatio;
        let newHeaderHeight: number = (displayHeight - movementRange) / 2;
        let newFooterHeight: number = (displayHeight - movementRange) / 2;
        if (headerHeight > newHeaderHeight) {
          const headerDiff: number = headerHeight - newHeaderHeight;
          newFooterHeight = newFooterHeight - headerDiff;
          newHeaderHeight = newHeaderHeight + headerDiff;
        } else if (footerHeight > newFooterHeight) {
          const footerDiff: number = footerHeight - newFooterHeight;
          newFooterHeight = newFooterHeight + footerDiff;
          newHeaderHeight = newHeaderHeight - footerDiff;
        }
        headerHeight = newHeaderHeight;
        footerHeight = newFooterHeight;
      } else if (isMnDoc) {
        // 個人番号確認書類は中心に配置したいのでヘッダ/フッダを高い方に合わせて計算する
        headerHeight = footerHeight = Math.max(headerHeight, footerHeight);
      }
      // ヘッダとフッタの高さは0だとバリデーションに引っかかるので最小値は1にする
      if (headerHeight < 1) headerHeight = 1;
      if (footerHeight < 1) footerHeight = 1;

      // 画面と画像の比率を確認
      const aspectPicture: number = pictureWidth / pictureHeight;
      const aspectDisplay: number = displayWidth / displayHeight;
      const relativeRatioX: number = pictureWidth / displayWidth;
      const relativeRatioY: number = pictureHeight / displayHeight;
      const relativeRatio1: number = aspectPicture < aspectDisplay ? relativeRatioX : relativeRatioY;
      // 縦横のどちらかを画像と同じ大きさに拡大した場合の画面等の大きさ
      // 画面が画像に収まるように縦と横のどちらを合わせるかを決めている
      const pictureSpaceDisplayWidth: number = displayWidth * relativeRatio1;
      const pictureSpaceDisplayHeight: number = displayHeight * relativeRatio1;
      const pictureSpaceHeaderHeight: number = headerHeight * relativeRatio1;
      const pictureSpaceFooterHeight: number = footerHeight * relativeRatio1;

      if (isGpDoc || isOpDoc || isGeneralBook || isA4Shape) {
        // 汎用書類のマーカーの大きさは、スマホ(縦長)の場合は画面に合わせ、PC(横長)の場合は正方形にする
        const pictureSpaceHeaderFooterHeight: number = pictureSpaceHeaderHeight + pictureSpaceFooterHeight;
        const pictureSpaceDocumentHeight: number = pictureSpaceDisplayHeight - pictureSpaceHeaderFooterHeight;
        context.state.idDocAspectRatioView = Math.max(1.0, pictureSpaceDocumentHeight / pictureSpaceDisplayWidth);
      }

      // 最小限の移動範囲を確保した上での本人確認書類/個人番号書類のとりうる最大の大きさ
      const pictureSpaceMinMovementRange: number = isIdDoc && !isGeneralBook && !isA4Shape ? 25 : 0;
      const pictureSpaceCardMaxHeight: number = pictureSpaceDisplayHeight - pictureSpaceHeaderHeight - pictureSpaceFooterHeight - pictureSpaceMinMovementRange; // eslint-disable-line prettier/prettier
      const pictureSpaceCardMaxWidth1: number = pictureSpaceCardMaxHeight / context.state.idDocAspectRatioCalc;
      // 見た目上の違和感を無くすために最大の大きさを設定
      const pictureSpaceCardMaxWidth2: number =
        isGpDoc || isOpDoc || isGeneralBook || isA4Shape ? Number.POSITIVE_INFINITY : 1000 * relativeRatio1;
      // 画面縦幅よりもヘッダフッダ合計サイズの方が大きい場合は、横持ちや画面分割で異常なので、処理を打ち切る
      // TODO 撮影枠が小さすぎると撮影に支障が出るので、処理を打ち切るサイズを引き上げたい
      // TODO 処理を打ち切った際になんらかのログを残しておきたい
      if (pictureSpaceCardMaxHeight <= 0) throw new ApiCancelError('');

      // 本人確認書類が十分な移動量を確保するための画面/画像幅を計算
      const clippingPictureWidth: number = Math.floor(Math.min(pictureSpaceCardMaxWidth1, pictureSpaceCardMaxWidth2, pictureSpaceDisplayWidth)); // eslint-disable-line prettier/prettier
      const clippingDisplayWidth: number = Math.floor(clippingPictureWidth / relativeRatio1);

      let photoID: number = context.state.photoID[context.state.shootType] || -1;
      let positionY: number = 0.5;

      const isRandomWalk: boolean = isIdDoc;
      if (isRandomWalk) {
        let forceCenter: boolean =
          isIdDocDiagonal || isGeneralBook || isA4Shape || isGlobal || _isFront || _isFrontFace;
        if (context.state.idDocType === IdDocType.MyNumberCardAndBack && isIdDocBack) forceCenter = true;

        // 本人確認書類の画像IDとマーカー位置を取得する
        const response = await IdDocPhotos.location(
          {
            photo_type: isIdDocFront ? PhotoType.Front : isIdDocBack ? PhotoType.Back : PhotoType.Diagonal,
            photo: { width: clippingPictureWidth, height: pictureHeight },
            display: { width: clippingDisplayWidth, height: displayHeight },
            header: { width: clippingDisplayWidth, height: headerHeight },
            footer: { width: clippingDisplayWidth, height: footerHeight }
          },
          {
            card:
              isGeneralBook || isA4Shape
                ? undefined
                : { width: clippingDisplayWidth, height: clippingDisplayWidth * context.state.idDocAspectRatioCalc },
            forceCenter
          }
        );
        photoID = response.data.idDocPhotoID;
        positionY = response.data.positionY;
      } else {
        // 画像サイズを基準とした高さを計算
        const pictureSpaceDisplayHeight: number = displayHeight * (clippingPictureWidth / clippingDisplayWidth);
        const pictureSpaceHeaderHeight: number = headerHeight * (clippingPictureWidth / clippingDisplayWidth);
        const pictureSpaceFooterHeight: number = footerHeight * (clippingPictureWidth / clippingDisplayWidth);
        const pictureSpaceCardHeight: number = clippingPictureWidth * context.state.idDocAspectRatioCalc;

        // カードサイズを考慮しない可動範囲計算
        const top: number = Math.max(0, (pictureHeight - pictureSpaceDisplayHeight) / 2 + pictureSpaceHeaderHeight);
        const bot: number = Math.max(0, (pictureHeight - pictureSpaceDisplayHeight) / 2 + pictureSpaceFooterHeight);
        // 下辺基準の値を上辺基準に変換する
        const bottom: number = pictureHeight - bot;

        const minRatio: number = (top + pictureSpaceCardHeight / 2) / pictureHeight;
        const maxRatio: number = (bottom - pictureSpaceCardHeight / 2) / pictureHeight;
        positionY = 0.5 * (maxRatio - minRatio) + minRatio;
      }

      // 撮影位置を計算
      const pictureIdDocCenter: number = positionY * pictureHeight;
      const relativeRatio2: number = clippingDisplayWidth / clippingPictureWidth;
      const displaySpacePictureHeight: number = pictureHeight * relativeRatio2;
      const displaySpacePictureCenter: number = pictureIdDocCenter * relativeRatio2;
      const displayIdDocCenter: number = displaySpacePictureCenter - (displaySpacePictureHeight - displayHeight) / 2;

      // 本人確認書類/個人番号書類撮影開始
      const isKeepSize: boolean = isGpDoc || isOpDoc;
      context.commit('clippingDisplayWidth', clippingDisplayWidth);
      context.commit('rawBase64Width', isKeepSize ? pictureWidth : clippingPictureWidth);
      context.commit('rawBase64Height', pictureHeight);
      context.commit('pictureIdDocCenter', pictureIdDocCenter);
      context.commit('displayIdDocCenter', displayIdDocCenter);
      context.commit('photoID', { key: context.state.shootType, value: photoID });
    } else if (isFaceFront) {
      // 顔写真撮影開始
      context.commit('clippingDisplayWidth', displayWidth);
      context.commit('rawBase64Width', pictureWidth);
      context.commit('rawBase64Height', pictureHeight);
      context.commit('pictureIdDocCenter', pictureHeight * 0.5);
      context.commit('displayIdDocCenter', displayHeight * 0.5);
      context.commit('photoID', { key: context.state.shootType, value: -1 });
    }
  },

  // 撮影情報の送信 自動撮影
  async storeDetectData(context: ActionContext<State, RootState>, payload: string): Promise<void> {
    const isIdDocFront: boolean = context.state.shootType === 'identity-document-front';
    const isIdDocDiagonal: boolean = context.state.shootType === 'identity-document-diagonal';
    const isIdDocBack: boolean = context.state.shootType === 'identity-document-back';
    if (isIdDocFront || isIdDocDiagonal || isIdDocBack) {
      const photoID: number = context.state.photoID[context.state.shootType];
      const response = await IdDocPhotos.autoDetect(photoID, { imageBase64: payload });
      // 枠内判定、画像大きさ判定、角度判定、ブレ判定、光反射判定、暗さ判定、判定が存在しない場合はundefined
      const resultFrame = response.data.cvResults.find((result) => result.name === 'frame')?.result;
      const resultSize = response.data.cvResults.find((result) => result.name === 'size')?.result;
      const resultDiagonal = response.data.cvResults.find((result) => result.name === 'diagonal')?.result;
      const resultFaceQuality = response.data.cvResults.find((result) => result.name === 'face_quality')?.result;
      const resultSharpness = response.data.cvResults.find((result) => result.name === 'sharpness')?.result;
      const resultHighlight = response.data.cvResults.find((result) => result.name === 'highlight')?.result;
      const resultBrightness = response.data.cvResults.find((result) => result.name === 'brightness')?.result;
      // 判定に成功したらカウントアップし、失敗したらリセットする
      context.commit(response.data.isValid ? 'addDetectCount' : 'resetDetectCount');
      // 枠内判定、画像大きさ判定、角度判定のどれかが存在する場合、全てにおいて「存在しないもしくは判定成功」を満たしていたら枠を青くする
      const results: (boolean | undefined)[] = [resultFrame, resultSize, resultDiagonal];
      const isDetectCapturable: boolean = results.some((result) => result !== undefined);
      const isDetectCapture: boolean = results.every((result) => result !== false);
      if (isDetectCapturable) context.commit('isDetectCapture', isDetectCapture);
      if (!isDetectCapturable) context.commit('isDetectCapture', response.data.isValid);
      // 判定が存在してかつ判定失敗している場合にエラー文言を表示する 優先順位が高いほど下にある
      let detectWarningCode: string = '';
      if (resultBrightness === false) detectWarningCode = 'brightness';
      if (resultHighlight === false) detectWarningCode = 'highlight';
      if (resultSharpness === false) detectWarningCode = 'sharpness';
      if (resultFaceQuality === false) detectWarningCode = 'face_quality';
      if (!context.state.isDetectCapture) detectWarningCode = 'frame';
      context.commit('detectWarningCode', detectWarningCode);
    }
  },

  // 撮影情報の送信 手動/自動撮影
  async storeShootData(context: ActionContext<State, RootState>, payload: string): Promise<void> {
    const isIdDocFront: boolean = context.state.shootType === 'identity-document-front';
    const isIdDocDiagonal: boolean = context.state.shootType === 'identity-document-diagonal';
    const isIdDocBack: boolean = context.state.shootType === 'identity-document-back';
    const isMnDocFront: boolean = context.state.shootType === 'mynumber-document-front';
    const isMnDocBack: boolean = context.state.shootType === 'mynumber-document-back';
    const isFaceFront: boolean = context.state.shootType === 'face-front';

    const isIdDoc: boolean = isIdDocFront || isIdDocDiagonal || isIdDocBack;
    const isGpDoc: boolean = context.getters['isGpdoc'];
    const isOpDoc: boolean = context.getters['isOpdoc'];

    if (isIdDoc) {
      const photoID: number = context.state.photoID[context.state.shootType];
      await IdDocPhotos.upload(photoID, { imageBase64: payload, isAutoShoot: context.state.isDetectStart });
    } else if (isMnDocFront) {
      const response = await MnDocPhotos.upload({ imageBase64: payload, photoType: PhotoType.Front });
      context.commit('photoID', { key: context.state.shootType, value: response.data.myNumberDocPhotoId });
    } else if (isMnDocBack) {
      const response = await MnDocPhotos.upload({ imageBase64: payload, photoType: PhotoType.Back });
      context.commit('photoID', { key: context.state.shootType, value: response.data.myNumberDocPhotoId });
    } else if (isGpDoc) {
      const pageNumber: number = context.getters['gpdocPageCurr'];
      const response = await GpDocPhotos.upload({ imageBase64: payload, pageNumber });
      context.commit('photoID', { key: context.state.shootType, value: response.data.generalDocPhotoId });
    } else if (isOpDoc) {
      const optionalDocType: string = context.state.opDocType[context.getters['opdocTermCurr'] - 1];
      const pageNumber: number = context.getters['opdocPageCurr'];
      const response = await OpDocPhotos.upload({ imageBase64: payload, optionalDocType, pageNumber });
      context.commit('photoID', { key: context.state.shootType, value: response.data.optionalDocPhotoId });
    } else if (isFaceFront) {
      const response = await FaceFrontPhotos.upload({ imageBase64: payload });
      context.commit('photoID', { key: context.state.shootType, value: response.data.faceFrontPhotoID });
    }
  },

  // 撮影情報の取得
  async storeTrimmedPhotoData(context: ActionContext<State, RootState>): Promise<void> {
    const isIdDocFront: boolean = context.state.shootType === 'identity-document-front';
    const isIdDocDiagonal: boolean = context.state.shootType === 'identity-document-diagonal';
    const isIdDocBack: boolean = context.state.shootType === 'identity-document-back';
    const isMnDocFront: boolean = context.state.shootType === 'mynumber-document-front';
    const isMnDocBack: boolean = context.state.shootType === 'mynumber-document-back';
    const isFaceFront: boolean = context.state.shootType === 'face-front';

    const isIdDoc: boolean = isIdDocFront || isIdDocDiagonal || isIdDocBack;
    const isMnDoc: boolean = isMnDocFront || isMnDocBack;
    const isGpDoc: boolean = context.getters['isGpdoc'];
    const isOpDoc: boolean = context.getters['isOpdoc'];

    if (isIdDoc) {
      const photoID: number = context.state.photoID[context.state.shootType];
      const response = await IdDocPhotos.images(photoID);
      context.commit('trimmedPhoto', { key: context.state.shootType, value: response.data.trimmedImageBase64 });
    } else if (isMnDoc) {
      const photoID: number = context.state.photoID[context.state.shootType];
      const response = await MnDocPhotos.images(photoID);
      context.commit('trimmedPhoto', { key: context.state.shootType, value: response.data.trimmedImageBase64 });
    } else if (isGpDoc) {
      const photoID: number = context.state.photoID[context.state.shootType];
      const response = await GpDocPhotos.images(photoID);
      context.commit('trimmedPhoto', { key: context.state.shootType, value: response.data.imageBase64 });
    } else if (isOpDoc) {
      const photoID: number = context.state.photoID[context.state.shootType];
      const response = await OpDocPhotos.images(photoID);
      context.commit('trimmedPhoto', { key: context.state.shootType, value: response.data.imageBase64 });
    } else if (isFaceFront) {
      const photoID: number = context.state.photoID[context.state.shootType];
      const response = await FaceFrontPhotos.images(photoID);
      context.commit('trimmedPhoto', { key: context.state.shootType, value: response.data.imageBase64 });
    }
  },

  // 確認結果の送信
  async storeConfirm(context: ActionContext<State, RootState>): Promise<void> {
    const isIdDocFront: boolean = context.state.shootType === 'identity-document-front';
    const isIdDocDiagonal: boolean = context.state.shootType === 'identity-document-diagonal';
    const isIdDocBack: boolean = context.state.shootType === 'identity-document-back';
    const isMnDocFront: boolean = context.state.shootType === 'mynumber-document-front';
    const isMnDocBack: boolean = context.state.shootType === 'mynumber-document-back';
    const isFaceFront: boolean = context.state.shootType === 'face-front';

    const isIdDoc: boolean = isIdDocFront || isIdDocDiagonal || isIdDocBack;
    const isMnDoc: boolean = isMnDocFront || isMnDocBack;
    const isGpDoc: boolean = context.getters['isGpdoc'];
    const isOpDoc: boolean = context.getters['isOpdoc'];

    if (isIdDoc) {
      const photoID: number = context.state.photoID[context.state.shootType];
      await IdDocPhotos.confirm(photoID);
    } else if (isMnDoc) {
      const photoID: number = context.state.photoID[context.state.shootType];
      await MnDocPhotos.confirm(photoID);
    } else if (isGpDoc) {
      const photoID: number = context.state.photoID[context.state.shootType];
      await GpDocPhotos.confirm(photoID);
    } else if (isOpDoc) {
      const photoID: number = context.state.photoID[context.state.shootType];
      await OpDocPhotos.confirm(photoID);
    } else if (isFaceFront) {
      const photoID: number = context.state.photoID[context.state.shootType];
      await FaceFrontPhotos.confirm(photoID);
    }
  },

  useNextCameraMode(context: ActionContext<State, RootState>, payload: FacingMode): void {
    const videoDevices: CameraDevice[] = context.state.videoDevices;
    const unavailableCameras: string[] = context.state.unavailableCameras[payload];
    const currIndex: number = context.state.selectedDeviceIndex[payload];
    let nextIndex: number = -1;
    for (let i = 0; i < videoDevices.length; i++) {
      const index: number = (currIndex + i + 1) % videoDevices.length;
      const deviceId: string = videoDevices[index].deviceId;
      if (unavailableCameras.includes(deviceId)) continue;
      nextIndex = index;
      break;
    }

    const isReady: boolean = context.state.readyCameraModeModal;
    const isOnly: boolean = videoDevices.length - unavailableCameras.length === 1;
    if (isReady && isOnly) context.commit('setShowCameraModeModal', true);

    if (nextIndex !== currIndex) context.commit('setCameraModeIndex', { mode: payload, index: nextIndex });
  }
};

export const camera: Module<State, RootState> = {
  namespaced: true,
  state: createState,
  getters,
  mutations,
  actions
};
